/**
 * Speech-to-text integration — multi-provider.
 *
 * Two STT providers:
 * 1. **OpenAI Whisper** (primary) — when user has an API key in localStorage
 *    VAD captures audio → WAV → Whisper API → transcript text
 * 2. **Web Speech API** (fallback) — browser-native, no API key needed
 *    VAD detects speech boundaries, Web Speech transcribes in real-time
 *
 * Provider selection is handled by the useVoice hook at startup.
 */

import { STORAGE_KEYS } from '../utils/constants';

// ── Types ──

/** Active STT provider identifier */
export type SttProvider = 'whisper' | 'web-speech' | 'none';

/** User-chosen STT engine preference */
export type SttEngine = 'whisper' | 'web-speech';

// ── Audio Encoding ──

/**
 * Encode a Float32Array of 16kHz PCM samples into a WAV Blob.
 */
export function encodeAudioBlob(
  audioData: Float32Array,
  sampleRate: number,
): Blob {
  const length = audioData.length;
  const buffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * 2, true);

  for (let i = 0; i < length; i++) {
    const s = Math.max(-1, Math.min(1, audioData[i]!));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Convert a Blob to base64 string.
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ── Provider Detection ──

/** Check if an OpenAI API key is stored in localStorage */
export function hasOpenAIKey(): boolean {
  const key = localStorage.getItem(STORAGE_KEYS.OPENAI_API_KEY);
  return !!key && key.trim().length > 0;
}

/** Get the stored OpenAI API key */
export function getOpenAIKey(): string {
  return localStorage.getItem(STORAGE_KEYS.OPENAI_API_KEY)?.trim() ?? '';
}

/** Check if Web Speech API is available */
export function isWebSpeechAvailable(): boolean {
  return !!(
    (window as unknown as Record<string, unknown>).SpeechRecognition ??
    (window as unknown as Record<string, unknown>).webkitSpeechRecognition
  );
}

/** Get the user's stored STT engine preference (if any). */
export function getSttEngine(): SttEngine | null {
  const val = localStorage.getItem(STORAGE_KEYS.STT_ENGINE);
  if (val === 'whisper' || val === 'web-speech') return val;
  return null;
}

/** Compute the default engine: Whisper if an API key exists, else Web Speech. */
export function defaultSttEngine(): SttEngine {
  return hasOpenAIKey() ? 'whisper' : 'web-speech';
}

/**
 * Determine the best available STT provider, respecting the user's engine
 * preference stored in localStorage.
 *
 * - "whisper" selected AND API key present → whisper
 * - "whisper" selected but no API key     → fall back to web-speech
 * - "web-speech" selected                 → web-speech (regardless of key)
 * - No preference stored                  → backwards-compatible auto-detect
 */
export function detectSttProvider(): SttProvider {
  const engine = getSttEngine() ?? defaultSttEngine();

  if (engine === 'whisper') {
    if (hasOpenAIKey()) return 'whisper';
    // Whisper requested but no key — fall back
    if (isWebSpeechAvailable()) return 'web-speech';
    return 'none';
  }

  // engine === 'web-speech'
  if (isWebSpeechAvailable()) return 'web-speech';
  return 'none';
}

// ══════════════════════════════════════════════════
// ██  OpenAI Whisper Transcriber
// ══════════════════════════════════════════════════

/**
 * Transcribe a WAV blob using the OpenAI Whisper API.
 *
 * @param audioBlob - WAV audio blob
 * @param apiKey - OpenAI API key
 * @returns Transcript text, or empty string on failure
 */
export async function transcribeWithWhisper(
  audioBlob: Blob,
  apiKey: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', audioBlob, `voice-${Date.now()}.wav`);
  formData.append('model', 'whisper-1');

  const response = await fetch(
    'https://api.openai.com/v1/audio/transcriptions',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'unknown');
    throw new Error(
      `Whisper API error ${response.status}: ${errorText}`,
    );
  }

  const data = (await response.json()) as { text?: string };
  return data.text?.trim() ?? '';
}

// ══════════════════════════════════════════════════
// ██  Web Speech API Transcriber
// ══════════════════════════════════════════════════

// Type definitions for Web Speech API

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly [index: number]: { readonly transcript: string };
  readonly length: number;
}

interface SpeechRecognitionResultList {
  readonly [index: number]: SpeechRecognitionResult;
  readonly length: number;
}

interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
  readonly resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

export interface SpeechTranscriberOptions {
  /** BCP-47 language tag (default: "en-US") */
  lang?: string;
  /** Called with the latest interim transcript text for UI display */
  onInterim?: (text: string) => void;
}

/**
 * Web Speech API transcriber designed to work alongside VAD.
 *
 * Lifecycle:
 * 1. `start()` — begin recognition (when voice mode activates)
 * 2. `resetTranscript()` — clear buffer (on VAD speech start)
 * 3. `finalize()` — stop, wait for final results, return transcript (on VAD speech end)
 *    Automatically restarts for the next utterance.
 * 4. `destroy()` — tear down (when voice mode deactivates)
 */
export class SpeechTranscriber {
  private recognition: SpeechRecognitionInstance | null = null;
  private isRunning = false;

  /** Accumulated final transcript segments for current utterance */
  private finalTranscript = '';
  /** Latest interim (not-yet-final) text */
  private pendingInterim = '';

  /** Resolve function for an in-progress finalize() call */
  private finalizeResolve: (() => void) | null = null;

  private onInterim?: (text: string) => void;

  constructor(options?: SpeechTranscriberOptions) {
    const SpeechRecognition =
      (window as unknown as Record<string, unknown>).SpeechRecognition ??
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('[stt] Web Speech API not available in this browser');
      return;
    }

    this.onInterim = options?.onInterim;

    const recognition = new (SpeechRecognition as new () => SpeechRecognitionInstance)();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = options?.lang ?? 'en-US';

    recognition.addEventListener('result', ((e: Event) => {
      const event = e as SpeechRecognitionEvent;

      let final = '';
      let interim = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i]!;
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      this.finalTranscript = final;
      this.pendingInterim = interim;

      const fullText = (final + ' ' + interim).trim();
      if (fullText) {
        this.onInterim?.(fullText);
      }
    }) as EventListener);

    recognition.addEventListener('error', ((e: Event) => {
      const event = e as SpeechRecognitionErrorEvent;
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('[stt] Recognition error:', event.error);
      }
    }) as EventListener);

    recognition.addEventListener('end', () => {
      if (this.finalizeResolve) {
        this.finalizeResolve();
        this.finalizeResolve = null;
        return;
      }

      if (this.isRunning) {
        try {
          recognition.start();
        } catch {
          // Ignore — may be in invalid state
        }
      }
    });

    this.recognition = recognition;
  }

  /** Whether the Web Speech API is available */
  get isAvailable(): boolean {
    return this.recognition !== null;
  }

  /** Start continuous recognition. */
  start(): void {
    if (!this.recognition || this.isRunning) return;
    this.isRunning = true;
    this.clearBuffers();
    try {
      this.recognition.start();
      console.log('[stt] Web Speech recognition started');
    } catch {
      console.warn('[stt] Failed to start recognition');
    }
  }

  /** Reset transcript buffers (call on VAD speech start). */
  resetTranscript(): void {
    if (this.recognition && this.isRunning) {
      this.clearBuffers();
      try {
        this.recognition.abort();
      } catch {
        // Ignore
      }
    } else {
      this.clearBuffers();
    }
  }

  /**
   * Finalize the current utterance: stop, wait for final results, return transcript.
   * Automatically restarts for the next utterance.
   */
  async finalize(): Promise<string> {
    if (!this.recognition) {
      return this.getTranscript();
    }

    this.isRunning = false;

    return new Promise<string>((resolve) => {
      const timeout = setTimeout(() => {
        this.finalizeResolve = null;
        const transcript = this.getTranscript();
        console.log('[stt] Finalize timeout, transcript:', transcript || '(empty)');
        this.restartForNextUtterance();
        resolve(transcript);
      }, 1500);

      this.finalizeResolve = () => {
        clearTimeout(timeout);
        const transcript = this.getTranscript();
        console.log('[stt] Finalized transcript:', transcript || '(empty)');
        this.restartForNextUtterance();
        resolve(transcript);
      };

      try {
        this.recognition!.stop();
      } catch {
        clearTimeout(timeout);
        this.finalizeResolve = null;
        const transcript = this.getTranscript();
        this.restartForNextUtterance();
        resolve(transcript);
      }
    });
  }

  /** Fully destroy the transcriber. */
  destroy(): void {
    this.isRunning = false;
    this.finalizeResolve = null;
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch {
        // Ignore
      }
    }
    this.recognition = null;
    this.clearBuffers();
    console.log('[stt] Transcriber destroyed');
  }

  // ── Internal ──

  private getTranscript(): string {
    return (this.finalTranscript + ' ' + this.pendingInterim).trim();
  }

  private clearBuffers(): void {
    this.finalTranscript = '';
    this.pendingInterim = '';
  }

  private restartForNextUtterance(): void {
    this.clearBuffers();
    this.isRunning = true;
    try {
      this.recognition?.start();
    } catch {
      setTimeout(() => {
        try {
          this.recognition?.start();
        } catch {
          console.warn('[stt] Failed to restart recognition');
        }
      }, 100);
    }
  }
}
